import { useState, type FC, useMemo } from "react";
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Abonnement } from "../../lib/interfaces/abonnement.interface";
import Divider from "../ui/Divider";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { z } from "zod";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import { Alert } from "../ui/alert";
import { useCreateAjoutRuchesStripeSession } from "../../lib/hooks/usePaiement";

interface Props {
  abonnement: Abonnement;
  open: boolean;
  setOpen: (val: boolean) => void;
}

const AugmenterCheptelDialog: FC<Props> = ({ open, setOpen, abonnement }) => {
  const [ruches, setRuches] = useState("");
  const createStripeSessionMutation = useCreateAjoutRuchesStripeSession();

  const total = useMemo(() => {
    const nbRuches = +ruches;
    if (isNaN(nbRuches)) return 0;
    return nbRuches * abonnement.pack!.prix;
  }, [ruches, abonnement]);

  const valider = async () => {
    const parsedRuches = z.coerce.number().int().positive().safeParse(ruches);
    if (!parsedRuches.success) {
      toast.error("Le nombre de ruches doit être un entier positif");
      return;
    }
    const nbRuches = parsedRuches.data;
    createStripeSessionMutation.mutate(
      {
        abonnementId: abonnement.id,
        ruches: nbRuches,
      },
      {
        onSuccess: async (stripeCheckoutSessionId) => {
          const stripe = await loadStripe(`${import.meta.env.VITE_STRIPE_KEY}`);
          await stripe?.redirectToCheckout({ sessionId: stripeCheckoutSessionId });
        },
        onError: (error: any) => {
          console.error(error);
          toast.error(error.response?.data?.message || "Une erreur s'est produite. Veuillez réssayer ultérieurement.");
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className='max-w-[650px]'>
        <DialogHeader>
          <DialogTitle>Augmenter mon cheptel</DialogTitle>
        </DialogHeader>
        {/* Body */}
        <Divider />
        <Alert className='text-sm bg-orange-50 border-orange-200'>
          <div className='text-orange-700 font-semibold mb-1'>Important</div>
          Il ne s'agit pas d'un réabonnement mais simplement d'une augmentation du nombre de ruches assurées.
          <br />
          De plus, l'augmentation de ne sera valable que pour la période d'abonnement en cours.
        </Alert>
        <div>
          <span className='font-semibold text-gray-900'>Votre pack d'options :</span> {abonnement.pack!.nom}
          <br />
          <span className='font-semibold text-gray-900'>Assurance :</span> {abonnement.pack!.type_assurance?.nom}
          <br />
          <span className='font-semibold text-gray-900'>Eco-contribution :</span> Incluse
          <div className='mt-2'>
            <span className='font-semibold text-gray-900'>Nombre de ruches couvertes actuellement :</span>{" "}
            <span className='font-bold text-green-700'>{abonnement.ruches}</span>
          </div>
          <div className='my-4'>
            <Label htmlFor='ruches'>Nombre de ruches à ajouter</Label>
            <Input
              min='0'
              className='max-w-[250px] mt-0.5'
              type='number'
              id='ruches'
              required
              value={ruches}
              onChange={(e) => setRuches(e.currentTarget.value)}
            />
          </div>
          <div className='border-l-2 pl-4'>
            <span className='font-semibold text-gray-900'>Prix par ruche :</span>{" "}
            {(abonnement.pack!.prix / 100).toFixed(2)} € TTC / ruche
            <br />
            <span className='font-semibold text-gray-900'>TOTAL dû :</span> {(total / 100).toFixed(2)} TTC
          </div>
        </div>
        {/* Footer */}
        <DialogFooter>
          <div>
            <div className='flex flex-row gap-5 justify-end'>
              <DialogClose asChild>
                <Button variant={"outline"}>Annuler</Button>
              </DialogClose>
              <Button
                disabled={total < 50 || createStripeSessionMutation.isPending}
                className='min-w-[240px]'
                onClick={valider}
                loading={createStripeSessionMutation.isPending}
              >
                Confirmer et s'engager à payer {(total / 100).toFixed(2)} €
              </Button>
            </div>
            <div className='text-right text-xs h-1 text-red-400 mt-0.5'>
              {ruches !== "" &&
                total < 50 &&
                "Indisponible pour les paiements inférieurs à 0.50€. Nous contacter directement."}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AugmenterCheptelDialog;
